import {Observer} from "mobx-react";
import type {MouseEvent} from "react";
import React, {Fragment, useEffect, useLayoutEffect, useReducer, useRef, useState} from "react";
import styled from "styled-components";
import {CountdownCircleTimer} from "react-countdown-circle-timer";
import {ReactUtils} from "../../utils/ReactUtils";
import {useAppStore} from "../../../StateManager";
import ListIcon from "../icons/list.svg?react";
import ChartIcon from "../icons/chart-line.svg?react";
import FrameIcon from "../icons/frame.svg?react";
import CloseIcon from "../icons/xmark.svg?react";
import InfoIcon from "../icons/info.svg?react";
import ChevronDownDouble from "../icons/chevron-down-double.svg?react";
import EyeIcon from "../icons/eye-alt.svg?react";
import EyeSlashIcon from "../icons/eye-slash-alt.svg?react";
import TrashIcon from "../icons/trash.svg?react";
import CheckIcon from "../icons/blue-circle-checkbox.svg?react";
import {XyiconFeature} from "../../../generated/api/base";
import {IconButtonV5, IconButtonStyled} from "../interaction/IconButtonV5";
import {colorPalette} from "../styles/colorPalette";
import {baseDistance, ELLIPSIS, FLEXCENTER, fontSize, radius, zIndex} from "../styles/styles";
import {useClickOutside} from "../utils";
import type {TransformObj} from "../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../utils/dom/DomUtils";
import {DomPortal} from "../../modules/abstract/portal/DomPortal";
import {NavigationEnum} from "../../../Enums";
import {iconSet, isDefaultViewNavigationIcon} from "../navigation/views/ViewIconLibrary";
import {StringUtils} from "../../../utils/data/string/StringUtils";
import type {Space} from "../../../data/models/Space";
import {
	addDetailsViewToTabsIfNeededAndSelect,
	EventNameForViewsChangeInLocalStorage,
	getActiveWorkspaceViewTabFromLocalStorage,
	getWorkspaceViewTabsFromLocalStorage,
	onWorkspaceViewClick,
	removeAllWorkspaceViewTabsExceptSelected,
	removeWorkspaceViewFromTabs,
	selectDetailsViewTab,
} from "./ViewTabsCommon";
import {LineStyled} from "./ProjectName.styled";

const horizontalGapSize = 8;
const minTabWidth = 112;
const maxTabWidth = 224;

const getNavigationIcon = (iconName: string) => {
	const matchingIcon = !isDefaultViewNavigationIcon(iconName) ? iconSet.find((icon) => icon.label === iconName)?.IconComponent : undefined;
	const NavigationIcon = matchingIcon;

	const icon = iconName;

	if (icon === "loading") {
		return "loading";
	}
	if (icon === "ready") {
		return CheckIcon;
	}

	// chart, frame, or default → use matchingIcon override if available
	if (icon === "chart") {
		return NavigationIcon || ChartIcon;
	}
	if (icon === "info") {
		return NavigationIcon || InfoIcon;
	}
	if (icon === "frame") {
		return NavigationIcon || FrameIcon;
	}

	// fallback - list icon for everything else
	return NavigationIcon || ListIcon;
};

interface IViewSimple {
	id: string;
	name: string;
	icon: "list" | "frame" | "chart" | "loading" | "ready" | "info";
	iconColor: string;
	isActive: boolean;
}

export const ViewTabsV5 = () => {
	const appState = useAppStore((state) => state.appState);
	const navBarState = useAppStore((state) => state.navBarState);
	const viewTabsWrapperRef = useRef<HTMLDivElement>(null);
	const dropDownRef = useRef<HTMLDivElement>(null);
	const chevronDownRef = useRef<HTMLDivElement>(null);
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
	const [areTabsVisible, setAreTabsVisible] = useState<boolean>(true);
	const [maxNumberOfViewsInTopBar, setMaxNumberOfViewsInTopBar] = useState<number>(1);
	const [lastViewedSpaceName, setLastViewedSpaceName] = useState<string>("");
	const [dropDownMenuTransformObject, setDropDownMenuTransformObject] = useState<TransformObj>(null);
	const [newTabSize, setNewTabSize] = useState<number>(0);
	const resizeObserver = useRef<ResizeObserver>(
		new ResizeObserver((entries: ResizeObserverEntry[]) => {
			forceUpdate();
		}),
	);
	const viewTabsInLocalStorage = getWorkspaceViewTabsFromLocalStorage(appState.user?.id, appState.organizationIdByAuthData, appState.actions);

	const activeViewTabInLocalStorage = getActiveWorkspaceViewTabFromLocalStorage(appState.user?.id, appState.organizationIdByAuthData);

	const onViewTabClick = (viewId: string, forcePutInBeginningOfList: boolean) => {
		if (StringUtils.xyiconOrBoundaryRefIdPattern.test(viewId)) {
			if (forcePutInBeginningOfList) {
				const currentTabs = getWorkspaceViewTabsFromLocalStorage(appState.user?.id, appState.organizationIdByAuthData, appState.actions);
				const existingTab = currentTabs.find((t) => t.viewId === viewId);

				addDetailsViewToTabsIfNeededAndSelect(
					existingTab ?? {
						viewId,
						lastUsed: Date.now(),
					},
					appState,
					true,
				);
			}

			selectDetailsViewTab(viewId, appState);
		} else {
			const view = appState.actions.getViewOrReportViewById(viewId);

			if (view && (view?.id !== activeViewTabInLocalStorage?.viewId || forcePutInBeginningOfList)) {
				onWorkspaceViewClick(view, forcePutInBeginningOfList);
			}
		}
	};

	const renderViewTab = (viewSimple: IViewSimple, forcePutInBeginningOfList: boolean) => {
		const isActive = viewSimple.isActive;

		const onCloseClick = (event: MouseEvent) => {
			event.stopPropagation();
			removeWorkspaceViewFromTabs(viewSimple.id, appState);
			forceUpdate();
			sessionStorage.clear();
		};

		const NavigationIconComponent = getNavigationIcon(viewSimple.icon);

		return (
			<Fragment key={viewSimple.id}>
				<ViewTabStyled
					className={ReactUtils.cls("viewTabs", {selected: isActive})}
					onClick={() => onViewTabClick(viewSimple.id, forcePutInBeginningOfList)}
					title={`${viewSimple?.name}${viewSimple.icon === "frame" ? `\n${lastViewedSpaceName}` : ""}`}
					$showCloseButton={views.length > 1}
					$isViewTab={!forcePutInBeginningOfList}
					$iconColor={viewSimple.iconColor}
					$newTab={views.length < 2 ? maxTabWidth : newTabSize}
				>
					<IconStyled
						className={ReactUtils.cls("icon", {
							viewIcon: true,
							loading: NavigationIconComponent === "loading",
							ready: NavigationIconComponent === CheckIcon,
						})}
					>
						{NavigationIconComponent === "loading" ? (
							<CountdownCircleTimer
								isPlaying
								duration={2}
								colors={["#3b82f6", "#60a5fa", "#93c5fd"]}
								colorsTime={[2, 1, 0]}
								strokeWidth={6}
								size={20}
								onComplete={() => {
									return {shouldRepeat: true, delay: 0.5};
								}}
							/>
						) : (
							<NavigationIconComponent />
						)}
					</IconStyled>
					<TabNameStyled>{viewSimple?.name}</TabNameStyled>
					<IconButtonV5
						IconComponent={CloseIcon}
						onClick={onCloseClick}
					/>
				</ViewTabStyled>
				{!forcePutInBeginningOfList && (
					<LineStyled
						$height={48}
						$width={1}
					/>
				)}
			</Fragment>
		);
	};

	const onToggleDropdown = () => {
		setIsDropdownOpen((o) => !o);
	};

	const onToggleTabsVisibility = () => {
		setAreTabsVisible((v) => !v);
		setIsDropdownOpen(false);
	};

	const onCloseOtherTabsClick = () => {
		removeAllWorkspaceViewTabsExceptSelected(appState);
		forceUpdate();
	};

	useClickOutside(
		[dropDownRef, chevronDownRef],
		() => {
			setIsDropdownOpen(false);
		},
		"mouseup",
	);

	useEffect(() => {
		if (appState.selectedMenu !== NavigationEnum.DETAILS) {
			return;
		}

		const splittedHash = appState.app.hash.split("/");
		const refIdMaybe = splittedHash[splittedHash.length - 1];
		if (!refIdMaybe) {
			return;
		}

		const currentTabs = getWorkspaceViewTabsFromLocalStorage(appState.user?.id, appState.organizationIdByAuthData, appState.actions);
		const existingTab = currentTabs.find((t) => t.viewId === refIdMaybe);

		addDetailsViewToTabsIfNeededAndSelect(
			existingTab ?? {
				viewId: refIdMaybe,
				lastUsed: Date.now(),
			},
			appState,
			false,
		);
	}, [appState, appState.app.hash, appState.selectedMenu, appState.organizationIdByAuthData, appState.user?.id, appState.actions]);

	useEffect(() => {
		window.addEventListener(EventNameForViewsChangeInLocalStorage, forceUpdate);

		return () => {
			window.removeEventListener(EventNameForViewsChangeInLocalStorage, forceUpdate);
		};
	}, []);

	useEffect(() => {
		const currentResizeObserver = resizeObserver.current;

		if (viewTabsWrapperRef.current) {
			currentResizeObserver.observe(viewTabsWrapperRef.current);
		}

		return () => {
			currentResizeObserver.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [viewTabsWrapperRef.current]);

	useEffect(() => {
		forceUpdate();
	}, [appState]);

	useEffect(() => {
		const renderLastViewedSpace = async () => {
			await appState.app.transport.services.feature.refreshList(XyiconFeature.Space);
			const userId = appState.user?.id;
			const orgId = appState.organizationIdByAuthData;
			const localStorageKeyForLastViewedSpace = appState.getLocalStorageKeyForLastViewedSpace(userId, orgId);
			const lastViewedSpaceId = localStorage.getItem(localStorageKeyForLastViewedSpace);
			const lastViewedSpace = lastViewedSpaceId
				? (appState.actions.getFeatureItemById(lastViewedSpaceId, XyiconFeature.Space) ??
					appState.actions.getFeatureItemByRefId<Space>(lastViewedSpaceId, XyiconFeature.Space))
				: null;

			if (lastViewedSpace && lastViewedSpace.name !== lastViewedSpaceName) {
				setLastViewedSpaceName(lastViewedSpace.name);
			}
		};

		renderLastViewedSpace();
	}, [appState, lastViewedSpaceName]);

	useEffect(() => {
		if (isDropdownOpen && chevronDownRef.current && dropDownRef.current) {
			setDropDownMenuTransformObject(
				DomUtils.getFixedFloatingElementPosition(
					chevronDownRef.current,
					dropDownRef.current,
					VerticalAlignment.bottomOuter,
					HorizontalAlignment.center,
					5,
					5,
				),
			);
		}
	}, [isDropdownOpen]);

	const views: IViewSimple[] = [];

	views.push(
		...viewTabsInLocalStorage
			.map((viewTab) => {
				let view = appState.actions.getViewOrReportViewById(viewTab.viewId);
				if (!view) {
					if (StringUtils.xyiconOrBoundaryRefIdPattern.test(viewTab.viewId)) {
						// It's a details view tab, eg.: p3/details/xyicon/x3
						return {
							id: viewTab.viewId,
							name: viewTab.viewId,
							icon: "info" as const,
							iconColor: "",
							isActive: viewTab.viewId === activeViewTabInLocalStorage?.viewId,
						};
					} else {
						return null;
					}
				}

				let icon = view.additionalDetails.navigationPanelData.icon;
				if (isDefaultViewNavigationIcon(icon)) {
					icon = "";
				}

				icon =
					icon ||
					(view.ownFeature === XyiconFeature.Report
						? viewTab.isLoading
							? "loading"
							: viewTab.unvisited
								? "ready"
								: "chart"
						: view.itemFeature === XyiconFeature.SpaceEditor
							? "frame"
							: "list");

				return {
					id: view.id,
					name: view.name,
					icon: icon as "chart" | "frame" | "list" | "loading" | "ready",
					iconColor: view.additionalDetails.navigationPanelData.color,
					isActive: view.id === activeViewTabInLocalStorage?.viewId,
				};
			})
			.filter((v) => !!v),
	);

	useLayoutEffect(() => {
		if (!viewTabsWrapperRef.current) {
			return;
		}

		const parentRect = viewTabsWrapperRef.current.getBoundingClientRect();
		const parentWidth = parentRect.width - 32;

		// Determine how many tabs can fit
		let newMaxNumberOfViewsInTopBar: number;

		// Check if all tabs fit at max width (with N separators - one after each tab)
		const separatorsForAll = views.length; // N separators for N tabs
		const totalWidthAtMax = views.length * maxTabWidth + separatorsForAll;

		if (totalWidthAtMax <= parentWidth) {
			newMaxNumberOfViewsInTopBar = views.length;
		} else {
			// Calculate how many tabs fit at min width
			// For N tabs: N * minTabWidth + N * 1px <= parentWidth
			// Solving: N * (minTabWidth + 1) <= parentWidth
			// N <= parentWidth / (minTabWidth + 1)
			const maxFit = Math.floor(parentWidth / (minTabWidth + 1));
			newMaxNumberOfViewsInTopBar = Math.max(1, Math.min(maxFit, views.length));
		}

		// Calculate equal width for the VISIBLE tabs (with N separators)
		const visibleSeparators = newMaxNumberOfViewsInTopBar;
		const availableForTabs = parentWidth - visibleSeparators;
		const calculatedWidth = availableForTabs / newMaxNumberOfViewsInTopBar;

		// Clamp width between min and max
		let widthPerTab: number;
		if (calculatedWidth > maxTabWidth) {
			widthPerTab = maxTabWidth;
		} else if (calculatedWidth < minTabWidth) {
			widthPerTab = minTabWidth;
		} else {
			widthPerTab = calculatedWidth;
		}

		// Update state if values changed
		if (newMaxNumberOfViewsInTopBar !== maxNumberOfViewsInTopBar) {
			setMaxNumberOfViewsInTopBar(newMaxNumberOfViewsInTopBar);
		}
		if (widthPerTab !== newTabSize) {
			setNewTabSize(widthPerTab);
		}
	}, [views.length, navBarState, appState.selectedMenu, maxNumberOfViewsInTopBar, newTabSize]);

	return (
		<Observer>
			{() => {
				const selectedMenu = appState.selectedMenu;

				if (
					[NavigationEnum.DETAILS, NavigationEnum.BOUNDARIES, NavigationEnum.XYICONS, NavigationEnum.SPACE, NavigationEnum.REPORT_VIEWS].includes(
						selectedMenu,
					)
				) {
					const viewsInTopBar = areTabsVisible ? views.slice(0, maxNumberOfViewsInTopBar) : [views.find((v) => v.isActive)];
					const viewsInDropdown = areTabsVisible ? views.slice(maxNumberOfViewsInTopBar) : views.filter((v) => !v.isActive);

					return (
						<ViewTabsWrapper ref={viewTabsWrapperRef}>
							<ViewTabsBarStyled className="ViewTabs">
								{viewsInTopBar.map((viewSimple) => (
									<React.Fragment key={viewSimple.id}>{renderViewTab(viewSimple, false)}</React.Fragment>
								))}
							</ViewTabsBarStyled>
							<RightSideStyled>
								{!areTabsVisible && (
									<IconButtonV5
										IconComponent={EyeIcon}
										onClick={onToggleTabsVisibility}
										title="Hidden Tabs: Click to Show"
										titleVerticalAlignment={VerticalAlignment.bottomOuter}
									/>
								)}
								<IconButtonV5
									IconComponent={ChevronDownDouble}
									onClick={onToggleDropdown}
									divRef={chevronDownRef}
									className={ReactUtils.cls({isOpen: isDropdownOpen})}
								/>
								{isDropdownOpen && (
									<DomPortal destination={appState.app.modalContainer}>
										<DropdownContentStyled
											ref={dropDownRef}
											style={{
												transform: dropDownMenuTransformObject?.translate || "none",
												position: "fixed",
												left: -4,
											}}
										>
											{viewsInDropdown.length > 0 && (
												<>
													<ViewInsideDropdownContainer>
														{viewsInDropdown.map((viewSimple) => (
															<React.Fragment key={viewSimple.id}>{renderViewTab(viewSimple, true)}</React.Fragment>
														))}
													</ViewInsideDropdownContainer>
													<HorizontalLine />
												</>
											)}
											<CloseAndHideOtherTabsStyled>
												<ViewTabStyled onClick={onCloseOtherTabsClick}>
													<IconStyled>
														<TrashIcon />
													</IconStyled>
													<TabNameStyled>Close Other Tabs</TabNameStyled>
												</ViewTabStyled>
												<ViewTabStyled onClick={onToggleTabsVisibility}>
													<IconStyled>{areTabsVisible ? <EyeSlashIcon /> : <EyeIcon />}</IconStyled>
													<TabNameStyled>{areTabsVisible ? "Hide Tabs" : "Show Tabs"}</TabNameStyled>
												</ViewTabStyled>
											</CloseAndHideOtherTabsStyled>
										</DropdownContentStyled>
									</DomPortal>
								)}
							</RightSideStyled>
						</ViewTabsWrapper>
					);
				}

				return <></>;
			}}
		</Observer>
	);
};

const HorizontalLine = styled.div`
	border-top: 1px solid ${colorPalette.gray.c300};
`;

const dropdownViewNumberScrollThreshold = 10;
const viewTabHeight = 32;
const dropdownPadding = 8;
const dropdownGap = 8;

const ViewTabStyled = styled.div<{$showCloseButton?: boolean; $isViewTab?: boolean; $iconColor?: string; $newTab?: number}>`
	cursor: ${(props) => (props.$showCloseButton ? "pointer" : "default")};
	display: flex;
	align-items: center;
	min-height: ${(props) => (props.$isViewTab ? "48px" : `${viewTabHeight}px`)};
	padding: 0 ${horizontalGapSize}px;
	gap: ${horizontalGapSize}px;
	position: relative;
	width: ${(props) => (props.$newTab ? `${props.$newTab}px` : `${minTabWidth}px`)};
	max-width: ${maxTabWidth}px;
	padding-right: ${baseDistance.sm};

	&.viewTabs {
		color: ${colorPalette.gray.c500Primary};
	}
	// svg current color get inherited from .viewIcon color property. This is to ensure the color is applied to the svg path properly.
	// Unless some icons are blurred (e.g. building.svg)
	&.viewTabs:not(.selected) {
		.viewIcon:not(.loading):not(.ready) {
			color: ${colorPalette.gray.c400};
		}
	}

	${IconButtonStyled} {
		min-width: 20px;
		width: 20px;
		height: 20px;
		visibility: ${(props) => (props.$showCloseButton ? "visible" : "hidden")};
		pointer-events: ${(props) => (props.$showCloseButton ? "auto" : "none")};
		position: static;
	}

	svg {
		width: 16px;
		height: 16px;
	}

	&.selected {
		background-color: ${colorPalette.white};
		color: black;

		.icon {
			fill: ${colorPalette.white};
		}

		.viewIcon:not(.loading) {
			color: ${(props) => (props.$iconColor ? `#${props.$iconColor}` : colorPalette.black)};
		}

		.closeIcon {
			background-color: ${colorPalette.gray.c700Dark};

			&:hover {
				background-color: ${colorPalette.primary.c800};
			}
		}
	}
`;

const ViewInsideDropdownContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${dropdownGap}px;
	max-height: ${dropdownViewNumberScrollThreshold * viewTabHeight + (dropdownViewNumberScrollThreshold - 1) * dropdownGap}px;
	overflow-y: auto;
`;

const CloseAndHideOtherTabsStyled = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${dropdownGap}px;

	${ViewTabStyled} {
		&:hover {
			background-color: ${colorPalette.gray.c200Light};
			border-radius: ${radius.sm};
		}
	}
`;

const TabNameStyled = styled.span`
	font-size: ${fontSize.md};
	line-height: 16px;
	${ELLIPSIS}
	flex: 1;
`;

const IconStyled = styled.div`
	min-width: 16px;
	width: 16px;
	height: 16px;
	${FLEXCENTER}
	fill: ${colorPalette.gray.c950};

	.icon {
		width: 12px;
		height: 12px;
		min-width: 12px;
		min-height: 12px;
	}

	&.closeIcon {
		position: absolute;
		right: ${baseDistance.sm};
		border-radius: ${radius.xs};
		background-color: ${colorPalette.gray.c200Light};

		&:hover {
			background-color: ${colorPalette.gray.c300};
		}
	}
`;

const DropdownContentStyled = styled.div`
	z-index: ${zIndex.contextOptions};
	display: flex;
	flex-direction: column;
	position: absolute;
	color: ${colorPalette.gray.c950};
	background-color: ${colorPalette.white};
	box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
	padding: ${dropdownPadding}px;
	border-radius: ${radius.md};
	gap: ${dropdownGap}px;

	${ViewTabStyled} {
		padding-right: ${baseDistance.sm};
		width: 165px;
		border-radius: ${radius.sm};

		&:hover {
			background-color: ${colorPalette.gray.c200Light};

			${IconButtonStyled} {
				display: flex;
			}
		}

		${TabNameStyled} {
			color: initial;
		}

		&.viewTabs:not(.selected) {
			.viewIcon:not(.loading):not(.ready) {
				color: initial;
			}
		}

		${IconButtonStyled} {
			color: initial;
			display: none;
		}
	}
`;

const RightSideStyled = styled.div`
	display: flex;
	align-items: center;
	position: relative;

	svg {
		width: 16px;
		height: 16px;
	}

	${DropdownContentStyled} {
		visibility: visible;
		min-width: initial;
	}

	${IconButtonStyled} {
		height: 24px;
		min-width: 24px;
		margin-left: 4px;
		margin-right: 4px;
	}
`;

const ViewTabsBarStyled = styled.div`
	display: flex;
	align-items: center;
	overflow-x: hidden;
`;

const ViewTabsWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: left;
	flex: 1;
	min-width: 0;
	overflow: hidden;
`;
